import Request from "../model/Request";
import Response from "../model/Response";
import {RestClient} from "../util/RestClient";
import {Config} from "../util/Config";
import PagedResponse from "../entities/PagedResponse";
import Pagination from "../entities/Pagination";
import DeliveryPerson from "../entities/DeliveryPerson";
import AddBalanceRequest from "../model/transaction/AddBalanceRequest";
import Balance from "../entities/Balance";
import DeliveryCost from "../entities/DeliveryCost";
import Geo from "../entities/Geo";
import TransferArticleRequest from "../model/transaction/TransferArticleRequest";

export module DeliveryPersonService {
    export async function get(
        page: number = 1,
        pageSize: number = 15,
        filter?: string,
        orderByGeosIdProximity: string[] = [],
        activeMoneyDeliveryBalances: boolean = false
    ): Promise<Response<PagedResponse<DeliveryPerson>>> {
        const url = Config.get("GET_DELIVERY_PERSON_URL", {page, pageSize, filter, activeMoneyDeliveryBalances});
        const request = new Request(url, orderByGeosIdProximity)
        const response = await RestClient.post(request)
        if (response.success) {
            const personsData = response.data?.data;
            const paginationData = response.data?.pagination;
            if (personsData && paginationData) {
                const data = personsData.map((t: any) => new DeliveryPerson(
                    t.id,
                    t.name,
                    t.homePhone,
                    t.phone,
                    t.email,
                    t.status,
                    t.createdAt,
                    t.articleStockTotalAmount,
                    t.geo && new Geo(t.geo.id, t.geo.name, t.geo.parentId, t.geo.level, t.geo.hasChildren, t.geo.parentInfo),
                    t.balances.map((b: any) => new Balance(b.id, b.amount, b.currency)),
                    t.activeMoneyDeliveryBalances.map((b: any) => new Balance(b.id, b.amount, b.currency)),
                    t.deliveryCosts.map((b: any) => new DeliveryCost(b.id,
                        new Geo(b.geo.id, b.geo.name, b.geo.parentId, b.geo.level, b.geo.hasChildren),
                        b.currency, b.cost, b.deliveryPersonId, b.service))
                ));
                const pagination = new Pagination(paginationData.currentPage, paginationData.pageSize, paginationData.elementsCount, paginationData.pageCount);
                return new Response<PagedResponse<DeliveryPerson>>(true, {data, pagination}, response.error)
            }
        }
        return new Response<PagedResponse<DeliveryPerson>>(false, undefined, response.error, response.status);
    }

    export async function find(id: string): Promise<Response<DeliveryPerson>> {
        const url = Config.get("FIND_DELIVERY_PERSON_URL", {id});
        const request = new Request(url)
        const response = await RestClient.get(request);
        if (response.success) {
            const t = response.data;
            if (t) {
                const client = new DeliveryPerson(
                    t.id,
                    t.name,
                    t.homePhone,
                    t.phone,
                    t.email,
                    t.status,
                    t.createdAt,
                    t.articleStockTotalAmount,
                    t.geo && new Geo(t.geo.id, t.geo.name, t.geo.parentId, t.geo.level, t.geo.hasChildren, t.geo.parentInfo),
                    t.balances.map((b: any) => new Balance(b.id, b.amount, b.currency)),
                    t.activeMoneyDeliveryBalances.map((b: any) => new Balance(b.id, b.amount, b.currency)),
                    t.deliveryCosts.map((b: any) => new DeliveryCost(b.id,
                        new Geo(b.geo.id, b.geo.name, b.geo.parentId, b.geo.level, b.geo.hasChildren),
                        b.currency, b.cost, b.deliveryPersonId, b.service))
                );
                return new Response<DeliveryPerson>(true, client, response.error);
            }
        }
        return new Response<DeliveryPerson>(true, undefined, response.error, response.status);
    }

    export async function getTotalBalances(): Promise<Response<any>> {
        const url = Config.get("GET_DELIVERY_PERSON_TOTAL_BALANCES_URL");
        const request = new Request(url);
        const response = await RestClient.get(request);
        if (response.success) {
            const personsData = response.data;
            return new Response<any>(true, personsData, response.error)
        }
        return new Response<PagedResponse<DeliveryPerson>>(false, undefined, response.error, response.status);
    }

    export async function create(person: DeliveryPerson): Promise<Response<any>> {
        const url = Config.get("CREATE_DELIVERY_PERSON_URL");
        const {geo, ...rest} = person;
        const request = new Request(url, {geoId: geo?.id, ...rest});
        return await RestClient.post(request);
    }

    export async function update(person: DeliveryPerson): Promise<Response<any>> {
        const url = Config.get("UPDATE_DELIVERY_PERSON_URL", {id: person.id});
        const {geo, ...rest} = person;
        const request = new Request(url, {geoId: geo?.id, ...rest});
        return await RestClient.put(request);
    }

    export async function remove(person: DeliveryPerson): Promise<Response<any>> {
        const url = Config.get("REMOVE_DELIVERY_PERSON_URL", {id: person.id});
        const request = new Request(url);
        return await RestClient.remove(request);
    }

    export async function addBalance(addBalanceRequest: AddBalanceRequest): Promise<Response<any>> {
        const url = Config.get("ADD_BALANCE_TO_DELIVERY_PERSON_URL", {id: addBalanceRequest.to});
        const request = new Request(url, addBalanceRequest);
        return await RestClient.post(request);
    }

    export async function transferBalance(fromId: string, addBalanceRequest: AddBalanceRequest): Promise<Response<any>> {
        const url = Config.get("TRANSFER_BALANCE_TO_DELIVERY_PERSON_URL", {id: fromId});
        const request = new Request(url, addBalanceRequest);
        return await RestClient.post(request);
    }

    export async function transferArticle(fromId: string, transferArticleRequest: TransferArticleRequest): Promise<Response<any>> {
        const url = Config.get("TRANSFER_ARTICLE_TO_DELIVERY_PERSON_URL", {id: fromId});
        const request = new Request(url, transferArticleRequest);
        return await RestClient.post(request);
    }

    export async function updatePassword(deliveryPersonId: string, password: string): Promise<Response<any>> {
        const url = Config.get("UPDATE_DELIVERY_PERSON_PASSWORD_URL", {id: deliveryPersonId});
        const request = new Request(url, {password});
        return await RestClient.put(request);
    }
}